import { ACTION_NAMES } from '@/constants/actionNames';
import { defineStore } from 'pinia';
import { toastError } from '@arritech/ui/Toast';
import i18n from '@/i18n';
import { RepositoryFactory } from '@/repositories';

const getCaseRepository = () => RepositoryFactory.get('case');
const t = i18n.global.t;

const createInitialState = () => ({
	caseDetails: null as any,
	caseEntities: null as any,
	documentTypes: null as any,
	loading: false,
	documentMeta: null as Record<string, any> | null,
	groupedDocuments: null as any,
	loadingDocuments: false,
	reloadTimeline: false,
	addToEntityTable: false,
	refreshEntities: false,
	refreshMainFiles: false,
	refreshMainTable: false,
	refreshMessages: false,
	refreshDocuments: false,
	refreshCaseHistory: false,
	entityStatuses: [] as Array<any>,
	statusComponentInfo: {} as Record<string, unknown>,
	updateCaseComponentInfo: {} as Record<string, unknown>,
});

export const useCaseDetailsStore = defineStore('caseDetailsStore', {
	state: createInitialState,
	getters: {
		isLoading: (state) => state.loading,
		getCaseDetails: (state) => state.caseDetails,
		shouldGetCaseEntities: (state) => state.caseEntities,
		shouldGetDocumentTypes: (state) => state.documentTypes,
		getGroupedDocuments: (state) => state.groupedDocuments,
		shouldReloadTimeline: (state) => state.reloadTimeline,
		shouldAddToEntityTable: (state) => state.addToEntityTable,
		shouldRefreshEntities: (state) => state.refreshEntities,
		shouldRefreshMainFiles: (state) => state.refreshMainFiles,
		shouldRefreshMainTable: (state) => state.refreshMainTable,
		shouldRefreshMessages: (state) => state.refreshMessages,
		shoudRefreshDocuments: (state) => state.refreshDocuments,
		shouldRefreshCaseHistory: (state) => state.refreshCaseHistory,
		getDocumentMeta: (state) => state.documentMeta,
		getEntityStatuses: (state) => state.entityStatuses,
		getStatusComponentInfo: (state) => state.statusComponentInfo,
		getUpdateCaseComponentInfo: (state) => state.updateCaseComponentInfo,
		getStatusByEntityAndLink:
			(state) => (entityId: string, entityLinkId: string) => {
				const matchingStatuses = state.entityStatuses
					.filter(
						(e) => e.entityId === entityId && e.entityLinkId === entityLinkId
					)
					.sort(
						(a, b) =>
							new Date(b.createdBy?.timestamp || 0).getTime() -
							new Date(a.createdBy?.timestamp || 0).getTime()
					);
				return matchingStatuses[0] || null;
			},
		getStatusesByEntityAndLink:
			(state) => (entityId: string, entityLinkId: string) => {
				return state.entityStatuses
					.filter(
						(e) => e.entityId === entityId && e.entityLinkId === entityLinkId
					)
					.sort(
						(a, b) =>
							new Date(b.createdBy?.timestamp || 0).getTime() -
							new Date(a.createdBy?.timestamp || 0).getTime()
					);
			},
	},
	actions: {
		async fetchCaseDetailsById({
			caseId,
			findPayload,
		}: {
			caseId: string;
			findPayload?: any;
		}) {
			try {
				const headers = { 'x-action-name': ACTION_NAMES.LOADED_CASE_DETAILS };
				const resp = await getCaseRepository().getCaseDetailsByID({
					caseId,
					payload: findPayload ?? {},
					headers,
				});
				if (resp.data.success) {
					this.setCaseDetails(resp.data.data);
				} else {
					toastError({
						title: t('cases.details.errors.loadCaseDetails'),
						message: resp.data.errors?.[0]?.message ?? '',
					});
				}
			} catch {
				toastError({ message: t('cases.details.errors.loadCaseDetails') });
			}
		},
		async fetchCaseEntities({
			caseId,
			findPayload,
		}: {
			caseId: string;
			findPayload?: any;
		}) {
			try {
				const resp = await getCaseRepository().getCaseEntitiesWithCaseID({
					merchantId: sessionStorage.getItem('merchant_id'),
					caseId,
					payload: findPayload || {
						page: 1,
						limit: 25,
						search: '',
						orderBy: 'created_by.timestamp',
						orderByDesc: true,
					},
				});

				if (resp.data.success) {
					this.documentMeta = buildDocumentMeta(resp.data.data);
					this.caseEntities = {
						...resp.data.data,
						persons: (resp.data.data.persons || []).map(
							(person: any) => person.person
						),
						companies: (resp.data.data.companies || []).map(
							(company: any) => company.company
						),
					};
					this.entityStatuses = resp.data.data.entityStatuses || [];
				} else {
					toastError({
						title: t('cases.details.errors.loadCaseEntities'),
						message: resp.data.errors?.[0]?.message ?? '',
					});
				}
			} catch {
				toastError({ message: t('cases.details.errors.loadCaseEntities') });
			}
		},
		async fetchGroupedDocuments(payload: string, forceRefresh = false) {
			if (this.loadingDocuments) {
				while (this.loadingDocuments) {
					await new Promise((resolve) => setTimeout(resolve, 1000));
				}
			}

			this.loadingDocuments = true;
			const payloadKey =
				typeof payload === 'string' ? payload : JSON.stringify(payload);
			const prevPayload = localStorage.getItem('fetchDocumentsPayload');
			if (
				!forceRefresh &&
				this.groupedDocuments !== null &&
				payloadKey === prevPayload
			) {
				this.loadingDocuments = false;
				return this.groupedDocuments;
			}

			localStorage.setItem('fetchDocumentsPayload', payloadKey);
			try {
				const resp = await getCaseRepository().getAllDocuments({ payload });
				const documents = sortDocuments(resp.data.data.documents);
				this.groupedDocuments = documents;
				return documents;
			} finally {
				this.loadingDocuments = false;
			}
		},
		async fetchDocumentTypesByMerchantId({
			findPayload,
		}: {
			findPayload: any;
		}) {
			try {
				const resp = await getCaseRepository().getDocumentTypesByMerchantId({
					payload: findPayload,
				});
				if (resp.data.success) {
					this.documentTypes = resp.data.data;
				} else {
					toastError({
						title: t('cases.details.errors.loadDocumentTypes'),
						message: resp.data.errors?.[0]?.message ?? '',
					});
				}
			} catch {
				toastError({ message: t('cases.details.errors.loadDocumentTypes') });
			}
		},
		setLoadingState(isLoading: boolean) {
			this.loading = isLoading;
		},
		setReloadTimeline(shouldReload: boolean) {
			this.reloadTimeline = shouldReload;
		},
		setAddToEntityTable(addToEntityTable: boolean) {
			this.addToEntityTable = addToEntityTable;
		},
		setRefreshEntities(shouldRefresh: boolean) {
			this.refreshEntities = shouldRefresh;
		},
		setGroupedDocuments(groupedDocuments: any) {
			this.groupedDocuments = groupedDocuments;
		},
		setRefreshMainFiles(shouldRefresh: boolean) {
			this.refreshMainFiles = shouldRefresh;
		},
		setRefreshMainTable(shouldRefresh: boolean) {
			this.refreshMainTable = shouldRefresh;
		},
		setRefreshMessages(shouldRefresh: boolean) {
			this.refreshMessages = shouldRefresh;
		},
		setRefreshDocuments(shouldRefresh: boolean) {
			this.refreshDocuments = shouldRefresh;
		},
		setRefreshCaseHistory(shouldRefresh: boolean) {
			this.refreshCaseHistory = shouldRefresh;
		},
		setEntityStatuses(entityStatuses: any[]) {
			this.entityStatuses = entityStatuses;
		},
		setCaseDetails(caseDetails: any) {
			this.caseDetails = caseDetails;
		},
		patchStatusComponentInfo(partial: Record<string, unknown>) {
			Object.assign(this.statusComponentInfo, partial);
		},
		patchUpdateCaseComponentInfo(partial: Record<string, unknown>) {
			Object.assign(this.updateCaseComponentInfo, partial);
		},
		resetSummaryComponentInfo() {
			this.statusComponentInfo = {};
			this.updateCaseComponentInfo = {};
		},
	},
});

const buildDocumentMeta = (payload: any) => {
	const meta: Record<string, any> = {};

	const processEntity = (entityName: string, cfmSessions: any) => {
		if (!cfmSessions?.documents || !cfmSessions?.sessions) return;

		const stepMeta: Record<string, { formTitle: string | null }> = {};
		cfmSessions.sessions.forEach((session: any) => {
			if (session.steps) {
				Object.values(session.steps).forEach((step: any) => {
					stepMeta[`${session.sessionId}_${step.stepNo}`] = {
						formTitle: step.settings?.form?.title || null,
					};
				});
			}
		});

		cfmSessions.documents.forEach((doc: any) => {
			if (!doc.groupedDocumentId) return;
			const stepKey = `${doc.sessionId}_${doc.stepNo}`;
			const step = stepMeta[stepKey] || { formTitle: null };
			let documentTitle = null;
			if (doc.checkType === 'Upload Document' && doc.outputs?.length > 0) {
				documentTitle = doc.outputs[0].fileName;
			}
			meta[doc.groupedDocumentId] = {
				sessionId: doc.sessionId,
				checkType: doc.checkType,
				entityName,
				formTitle: step.formTitle,
				documentTitle,
				timestamp: doc.timestamp,
			};
		});
	};

	if (payload.persons) {
		payload.persons.forEach((p: any) => {
			const person = p.person;
			const name = [person.name, person.surname].filter(Boolean).join(' ');
			processEntity(name, p.cfmSessions);
		});
	}

	if (payload.companies) {
		payload.companies.forEach((c: any) => {
			const name = c.company?.name || c.name || '';
			processEntity(name, c.cfmSessions);
		});
	}

	return meta;
};

const sortDocuments = (documents: any[]) => {
	documents.forEach((document) => {
		document.documentFiles.sort((file1: any, file2: any) => {
			if (file1.documentSide === 'Front') return -1;
			if (file1.documentSide === 'Back') return 1;
			if (file2.documentSide === 'Back') return -1;
			if (file2.documentSide === 'Front') return 1;
			return file1.fileName.localeCompare(file2.fileName);
		});
	});
	return documents;
};
